import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";

export interface MediaItem {
    id: string;
    title: string;
    filename: string;
    url: string | null;
    contentType: string;
    sizeBytes: number | null;
    folderTid: string | null;
}

interface MediaFolder {
    tid: string;
    title: string;
    parentTid: string | null;
}

interface Capabilities {
    backend: "docore" | "own";
    foldersSupported: boolean;
}

// Max image size accepted client-side. Keep in sync with the dropzone hint below
// and the server body limit (nginx client_max_body_size / PHP post_max_size must
// exceed this × ~1.37 for base64 inflation).
const MAX_UPLOAD_MB = 10;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

async function fileToBase64(file: File): Promise<string> {
    const buf = await file.arrayBuffer();
    let binary = "";
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
    return btoa(binary);
}

async function readDimensions(file: File): Promise<void> {
    // Reserved for future focal-point / dimension capture; no-op today.
    return Promise.resolve();
}

/**
 * Media library modal. Backed by the server /api/v1/media/* surface, which is
 * either do-core (public Documents in an "Email Assets" folder tree) or the
 * editor's own storage (single flat library). Folder controls appear only when
 * the backend supports folders.
 */
export function MediaManager({
    onPick,
    onClose,
}: {
    onPick: (item: MediaItem, backend: "docore" | "own") => void;
    onClose: () => void;
}) {
    const [caps, setCaps] = useState<Capabilities | null>(null);
    const [folders, setFolders] = useState<MediaFolder[]>([]);
    const [activeTid, setActiveTid] = useState<string | null>(null);
    const [items, setItems] = useState<MediaItem[] | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    // Load capabilities + folders once.
    useEffect(() => {
        (async () => {
            try {
                const c = await api.get<Capabilities>("/api/v1/media/capabilities");
                setCaps(c);
                const fs = await api.get<MediaFolder[]>("/api/v1/media/folders");
                setFolders(fs);
                setActiveTid(fs[0]?.tid ?? null);
            } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
            }
        })();
    }, []);

    const loadItems = useCallback((tid: string | null) => {
        api.post<MediaItem[]>("/api/v1/media/list", { folderTid: tid })
            .then(setItems)
            .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    }, []);

    useEffect(() => {
        if (activeTid !== null || caps?.backend === "own") loadItems(activeTid);
    }, [activeTid, caps, loadItems]);

    const handleFiles = async (files: FileList | File[]) => {
        setError(null);
        for (const file of files) {
            if (!file.type.startsWith("image/")) {
                setError(`${file.name} is not an image`);
                continue;
            }
            // Guard oversized files client-side: base64 inflates ~37%, so a file
            // near the server body limit is rejected with an opaque 413. Catch it
            // here with a clear "resize it" message before we even upload.
            if (file.size > MAX_UPLOAD_BYTES) {
                setError(
                    `${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB — too large to upload. ` +
                        `Please resize it to under ${MAX_UPLOAD_MB} MB and try again.`,
                );
                continue;
            }
            setBusy(`Uploading ${file.name}…`);
            try {
                await readDimensions(file);
                const dataBase64 = await fileToBase64(file);
                await api.post<MediaItem>("/api/v1/media/upload", {
                    folderTid: activeTid,
                    filename: file.name,
                    contentType: file.type,
                    dataBase64,
                });
            } catch (e) {
                const msg = e instanceof Error ? e.message : String(e);
                // A 413 can still slip through if the base64 body edges past the
                // server limit even for an under-limit file — show the same guidance.
                setError(
                    /\b413\b|too large|content too large|request entity/i.test(msg)
                        ? `${file.name} is too large to upload. Please resize it to under ${MAX_UPLOAD_MB} MB and try again.`
                        : msg,
                );
            }
        }
        setBusy(null);
        loadItems(activeTid);
    };

    const foldersFor = (parent: string | null) => folders.filter((f) => f.parentTid === parent);
    const rootTid = folders.find((f) => f.parentTid === null)?.tid ?? null;

    return (
        <div className="edm-modal-backdrop" role="dialog" aria-modal="true" aria-label="Media library">
            <div className="edm-modal media-modal">
                <div className="asset-head">
                    <h3>Media library {caps ? <span className="media-backend">· {caps.backend}</span> : null}</h3>
                    <button type="button" className="tb-btn" onClick={onClose} aria-label="Close">
                        ✕
                    </button>
                </div>

                <div className="media-body">
                    {caps?.foldersSupported && (
                        <aside className="media-folders" aria-label="Folders">
                            <FolderNode
                                folders={foldersFor(rootTid)}
                                allFolders={folders}
                                activeTid={activeTid}
                                rootTid={rootTid}
                                onSelect={setActiveTid}
                            />
                            {rootTid && (
                                <button
                                    type="button"
                                    className="tb-btn media-new-folder"
                                    data-testid="new-folder"
                                    onClick={async () => {
                                        const title = window.prompt("New folder name");
                                        if (!title) return;
                                        try {
                                            await api.post("/api/v1/media/folders", {
                                                title,
                                                parentTid: activeTid ?? rootTid,
                                            });
                                            setFolders(await api.get<MediaFolder[]>("/api/v1/media/folders"));
                                        } catch (e) {
                                            setError(e instanceof Error ? e.message : String(e));
                                        }
                                    }}
                                >
                                    + Folder
                                </button>
                            )}
                        </aside>
                    )}

                    <div className="media-main">
                        <div
                            className={`asset-dropzone${dragOver ? " over" : ""}`}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOver(true);
                            }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragOver(false);
                                void handleFiles(e.dataTransfer.files);
                            }}
                        >
                            Drop images here, or{" "}
                            <button type="button" className="tb-btn" onClick={() => fileRef.current?.click()} data-testid="media-upload">
                                choose files
                            </button>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/png,image/jpeg,image/gif,image/webp"
                                multiple
                                style={{ display: "none" }}
                                onChange={(e) => {
                                    if (e.target.files) void handleFiles(e.target.files);
                                    e.target.value = "";
                                }}
                            />
                            <div className="muted asset-hint">
                                PNG, JPEG, GIF or WebP, up to 10MB · stored{" "}
                                {caps?.backend === "docore" ? "in DO Core (public)" : "in the editor library"}
                            </div>
                        </div>

                        {busy && <p className="status-line">{busy}</p>}
                        {error && <p className="status-line error">{error}</p>}

                        <div className="asset-grid" data-testid="media-grid">
                            {items?.map((it) => (
                                <button
                                    key={it.id}
                                    type="button"
                                    className="asset-tile"
                                    title={it.filename}
                                    onClick={() => onPick(it, caps?.backend ?? "own")}
                                >
                                    {it.url ? (
                                        <img src={it.url} alt={it.filename} loading="lazy" />
                                    ) : (
                                        <span className="asset-name">{it.filename}</span>
                                    )}
                                    <span className="asset-name">{it.filename}</span>
                                    <span
                                        className="asset-delete"
                                        role="button"
                                        aria-label={`Delete ${it.filename}`}
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            if (window.confirm(`Delete ${it.filename}?`)) {
                                                await api.post(`/api/v1/media/${it.id}/delete`);
                                                loadItems(activeTid);
                                            }
                                        }}
                                    >
                                        ✕
                                    </span>
                                </button>
                            ))}
                            {items !== null && items.length === 0 && (
                                <p className="muted">No images here yet — upload one above.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function FolderNode({
    folders,
    allFolders,
    activeTid,
    rootTid,
    onSelect,
}: {
    folders: MediaFolder[];
    allFolders: MediaFolder[];
    activeTid: string | null;
    rootTid: string | null;
    onSelect: (tid: string) => void;
}) {
    return (
        <ul className="media-folder-list">
            {folders.map((f) => {
                const children = allFolders.filter((c) => c.parentTid === f.tid);
                return (
                    <li key={f.tid}>
                        <button
                            type="button"
                            className={`media-folder${activeTid === f.tid ? " active" : ""}`}
                            data-testid={`folder-${f.title.replace(/\W+/g, "-")}`}
                            onClick={() => onSelect(f.tid)}
                        >
                            {f.parentTid === null ? "📁" : "📂"} {f.title}
                        </button>
                        {children.length > 0 && (
                            <FolderNode
                                folders={children}
                                allFolders={allFolders}
                                activeTid={activeTid}
                                rootTid={rootTid}
                                onSelect={onSelect}
                            />
                        )}
                    </li>
                );
            })}
        </ul>
    );
}
